import { Store } from 'pullstate';
import { debounce } from 'lodash';
import { ConversationsApi } from 'aida-api-client';
import ApiService from 'services/api';
import { logger } from 'services/logs/logger';
import {
  setIsMessagesWindowScrolling,
  setIsSidebarCollapsed,
} from './app-context';
import { getFallbackModelName, loadUserLimits, UserStore } from './user';
import { sleep } from 'utils/async-utils';
import {
  FileType,
  ConversationFileType,
  ConversationIndexType,
  getUniqueSelectedFileContextIdsFromMessages,
  IndexType,
} from 'utils/file-utils';
import TimeLogger from 'utils/time-logger';
import { DataDogLog } from 'services/logs/datadog';

let apiConfig: any = null;
let api: ConversationsApi | null = null;

export type MessageItemType = {
  messageId: string;
  messageType: string;
  message: string;
  profileImg: string;
  datetimeCreated?: string;
  datetimeLastUpdated?: string;
  generationStatus?: string;
  userFeedback?: any;
  previousMessageId?: string;
  allowRetry?: boolean;
  associatedFileRecordIds?: any[];
  associatedIndexIds?: any[];
  isAutomaticSelection?: boolean;
};

export type CurrentConversationType = {
  hasLoaded: boolean;
  isLoading: boolean;
  messages: MessageItemType[];
  gptModel: string;
  gptModelAutomaticallySelected: boolean;
  id?: string;
  fileContexts: string[]; // File IDs that have been selected
  fileContextFiles: ConversationFileType[]; // All files that are available with isSelected flag
  fileIdsUsedInConversation: string[]; // File IDs that have been selected AND used in the conversation
  fileStats: {
    count: number; // Total number of files used in the conversation
    valid: number; // Number of files that are not errored
  };
  indexContexts: string[]; // Index IDs that have been selected
  indexContextIndexes: ConversationIndexType[]; // All indexes that are available with isSelected flag
  indexIdsUsedInConversation: string[]; // Index IDs that have been selected AND used in the conversation
  isStreaming: boolean;
  timeLogger: TimeLogger | null;
};

const initialState: CurrentConversationType = {
  hasLoaded: false,
  isLoading: false,
  id: undefined,
  messages: [],
  gptModel: '',
  gptModelAutomaticallySelected: false,
  fileContexts: [],
  fileContextFiles: [],
  fileIdsUsedInConversation: [],
  fileStats: {
    count: 0,
    valid: 0,
  },
  indexContexts: [],
  indexContextIndexes: [],
  indexIdsUsedInConversation: [],
  isStreaming: false,
  timeLogger: null,
};

export const CurrentConversationStore = new Store(initialState);

const DEFAULT_INDEX_CONTEXTS = ['franciscan_index'];

let lastProcessedMessageId: string | number | undefined = undefined;

CurrentConversationStore.createReaction(
  (s) => {
    const lastMessage = s.messages[s.messages.length - 1];
    return [
      lastMessage?.messageId,
      lastMessage?.messageType,
      s.isStreaming,
      s.messages.length,
    ] as const;
  },
  (selectorResult, draft) => {
    const [lastMessageId, lastMessageType, isStreaming, messagesLength] =
      selectorResult;
    if (
      messagesLength > 0 &&
      lastMessageType === 'userInput' &&
      !isStreaming &&
      lastMessageId !== lastProcessedMessageId &&
      api
    ) {
      lastProcessedMessageId = lastMessageId;

      const messages = draft.messages;
      const lastMessage = messages[messages.length - 1];

      draft.messages = [
        ...messages,
        {
          message: '',
          generationStatus: 'loading',
          messageId: 'NewMessage_' + (messages.length + 1).toString(),
          profileImg: '/system-profile.svg',
          messageType: 'generatedResponse',
          associatedFileRecordIds: [...draft.fileContexts],
          associatedIndexIds: [...draft.indexContexts],
        },
      ];
      draft.isStreaming = true;

      draft.timeLogger?.log('CurrentConversationStore.createReaction');
      const traceId = draft.timeLogger?.getSequenceId();
      sendMessageToApi(
        lastMessage,
        draft.id, // Can be undefined for new conversations - /messages endpoint will create one
        draft.gptModel || getFallbackModelName(),
        draft.fileContexts,
        draft.indexContexts,
        traceId,
      );
    }
  },
);

CurrentConversationStore.createReaction(
  (s) => [s.indexContexts],
  ([indexContexts], draft) => {
    let fileCount = 0;
    let validFiles: FileType[] = [];

    const indexCount = indexContexts.length;

    draft.fileStats = {
      count: fileCount + indexCount,
      valid: validFiles.length + indexCount,
    };
  },
);

export const setFileContextFiles = (files: FileType[]) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      fileContextFiles: files.map((f) => {
        const newf = { ...f } as ConversationFileType;
        if (s.fileContexts.includes(f.id)) {
          newf.isSelected = true;
        }
        return newf;
      }),
    };
  });
};

export const setIndexContextIndexes = (indexes: IndexType[]) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      indexContextIndexes: indexes.map((i) => {
        // Create a new object instead of trying to modify the original
        const newI: ConversationIndexType = {
          ...i, // Spread the original properties
          isSelected: s.indexContexts.includes(i.id), // Set isSelected based on context
        };
        return newI;
      }),
    };
  });
};

const getErrorMessage = (messageId) => {
  return {
    message: 'Unable to generate response',
    messageId: messageId,
    profileImg: '/system-profile.svg',
    generationStatus: 'error',
    messageType: 'generatedResponse',
    allowRetry: false,
  };
};

const setErrorState = (messageId) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      fileIdsUsedInConversation: Array.from(
        new Set([...s.fileIdsUsedInConversation, ...s.fileContexts]),
      ), // Even in error state we need to apply any temp files
      indexIdsUsedInConversation: Array.from(
        new Set([...s.indexIdsUsedInConversation, ...s.indexContexts]),
      ), // Even in error state we need to apply any temp indexes
      messages: [
        ...s.messages.slice(0, s.messages.length - 1),
        getErrorMessage(messageId),
      ],
    };
  });
};

const mapMessage = (m) => {
  if (m.messageType !== 'userInput' && !m.textContent) {
    return getErrorMessage(m.id);
  }

  return {
    ...m,
    messageId: m.id,
    messageType: m.messageType,
    message: m.textContent || '',
    userFeedback: m.userFeedback,
    profileImg:
      m.messageType === 'userInput' ? '/profile.svg' : '/system-profile.svg',
  };
};

let updateStreamedMessage = (result, messageId, messageType) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      messages: [
        ...s.messages.slice(0, s.messages.length - 1),
        {
          message: result,
          messageId: messageId,
          profileImg: '/system-profile.svg',
          generationStatus: 'pending',
          messageType: messageType,
        },
      ],
    };
  });
};

const debouncedUpdateStreamedMessage = debounce(updateStreamedMessage, 100);

let streamController;

export const validateStreamHeaders = (
  response: Response | null,
  conversationId: string,
  messageId: string,
) => {
  if (!response) {
    return;
  }

  const conversationIdHeader = response.headers.get('X-Conversation-Id');
  const messageIdHeader = response.headers.get('X-Message-Id');

  if (conversationIdHeader && conversationIdHeader !== conversationId) {
    logger.info(
      `Conversation ID mismatch: expected ${conversationId}, got ${conversationIdHeader}`,
    );
  }

  if (messageIdHeader && messageIdHeader !== messageId) {
    logger.info(
      `Message ID mismatch: expected ${messageId}, got ${messageIdHeader}`,
    );
  }
};

export const initializeStreamReader = async (
  conversationId: string,
  messageId: string,
) => {
  const timeLogger = CurrentConversationStore.getRawState().timeLogger;

  try {
    const streamResult = await ApiService.streamMessage(
      conversationId,
      messageId,
    );
    timeLogger?.log('ApiService.streamMessage');
    validateStreamHeaders(streamResult.response, conversationId, messageId);
    return streamResult.reader;
  } catch (e) {
    if ((e as any).name === 'AbortError') {
      logger.error('Streaming operation was cancelled', e);
      return null;
    }

    logger.error('Unable to stream message', e);
    setErrorState(messageId);
    return null;
  }
};

export const finalizeStream = async (conversationId: string) => {
  const timeLogger = CurrentConversationStore.getRawState().timeLogger;
  timeLogger?.logEnd(
    'End of performance tracking - finished all streaming and final data pulls',
  );

  const originalMessages =
    (await getMessagesByConversationId(conversationId)) || [];

  await CurrentConversationStore.update((s) => {
    const { messages, hasAutomaticSelection, gptModel } =
      getAutomaticModelSelectionData(originalMessages);

    return {
      ...s,
      gptModel: gptModel,
      gptModelAutomaticallySelected: hasAutomaticSelection,
      isStreaming: false,
      messages: messages,
      fileIdsUsedInConversation: Array.from(
        new Set([...s.fileIdsUsedInConversation, ...s.fileContexts]),
      ),
      indexIdsUsedInConversation: Array.from(
        new Set([...s.indexIdsUsedInConversation, ...s.indexContexts]),
      ),
    };
  });
  loadUserLimits();
};

export const handleStreamError = async (
  reader: ReadableStreamDefaultReader<Uint8Array> | null,
  conversationId: string,
  error: any,
) => {
  logger.error('stream failed -- possibly guardrailed', error);
  const timeLogger = CurrentConversationStore.getRawState().timeLogger;
  timeLogger?.log('Stream failed - possibly guardrailed ' + error);

  if (reader) {
    try {
      await reader.cancel();
    } catch (cancelError) {
      logger.debug('Reader cancellation error (expected)', cancelError);
    }
  }

  await finalizeStream(conversationId);
};

export const checkStreamTimeouts = (
  lastChunkTime: number,
  result: string,
): boolean => {
  const now = Date.now();
  const timeSinceLastChunk = now - lastChunkTime;
  const STREAM_TIMEOUT_MS = 30000;
  const CHUNK_TIMEOUT_MS = 5000;

  if (timeSinceLastChunk > CHUNK_TIMEOUT_MS && result.length > 0) {
    logger.info(
      `Stream inactive for ${timeSinceLastChunk}ms, checking if complete`,
    );
  }

  if (now - lastChunkTime > STREAM_TIMEOUT_MS) {
    logger.error('Stream timeout exceeded, aborting');
    return true;
  }

  return false;
};

export const processStreamChunk = (
  value: Uint8Array,
  decoder: TextDecoder,
  result: string,
  messageId: string,
  messageType: string,
  isFirstToken: boolean,
  timeLogger: TimeLogger | null,
): { result: string; isFirstToken: boolean } => {
  const decodedValue = decoder.decode(value, { stream: true });
  const newResult = result + decodedValue;

  if (isFirstToken && newResult) {
    timeLogger?.logEnd('WEB: FIRST TOKEN DISPLAYED');
  }

  debouncedUpdateStreamedMessage(newResult, messageId, messageType);

  return {
    result: newResult,
    isFirstToken: false,
  };
};

const streamNewMessage = async (
  conversationId: string,
  messageId: string,
  messageType: string,
) => {
  if (streamController) {
    streamController.abort();
  }

  streamController = new AbortController();

  const reader = await initializeStreamReader(conversationId, messageId);
  if (!reader) {
    return;
  }

  const decoder = new TextDecoder('utf-8');
  let result = '';
  let isFirstToken = true;
  let lastChunkTime = Date.now();
  const timeLogger = CurrentConversationStore.getRawState().timeLogger;

  while (reader && !streamController.signal.aborted) {
    try {
      if (checkStreamTimeouts(lastChunkTime, result)) {
        reader.cancel();
        throw new Error('Stream timeout exceeded');
      }

      const { done, value } = await reader.read();

      if (done) {
        await finalizeStream(conversationId);
        break;
      }

      lastChunkTime = Date.now();
      const chunkResult = processStreamChunk(
        value,
        decoder,
        result,
        messageId,
        messageType,
        isFirstToken,
        timeLogger,
      );
      result = chunkResult.result;
      isFirstToken = chunkResult.isFirstToken;
    } catch (ex) {
      await handleStreamError(reader, conversationId, ex);
      break;
    }
  }
};

const sendMessageToApi = async (
  message: any,
  conversationId: string | undefined,
  gptModel: string,
  associatedFileRecordIds?: string[],
  associatedIndexIds?: string[],
  traceId?: string,
) => {
  if (!api) {
    return;
  }

  const messageId = message?.messageId || message?.id;
  const messageText = message?.message || message?.textContent || '';

  const timeLogger = CurrentConversationStore.getRawState().timeLogger;

  try {
    const isNewConversation = !conversationId;
    const messages = await ApiService.createMessageWithConversation(
      {
        model: gptModel,
        textContent: messageText,
        associatedIndexIds: associatedIndexIds,
        traceId: traceId,
      },
      conversationId,
      isNewConversation ? messageText : undefined,
    );
    timeLogger?.log('ApiService.createMessageWithConversation');

    if (!messages || !Array.isArray(messages) || messages.length !== 2) {
      throw new Error('Invalid response from API - expected 2 messages');
    }

    const userMessage = messages[0];
    const assistantMessage = messages[1];
    const actualConversationId =
      userMessage.conversationId ||
      assistantMessage.conversationId ||
      conversationId;

    if (!actualConversationId) {
      throw new Error('No conversation ID returned from API');
    }

    if (isNewConversation) {
      CurrentConversationStore.update((s) => ({
        ...s,
        id: actualConversationId,
      }));
    }

    streamNewMessage(
      actualConversationId,
      assistantMessage.id,
      assistantMessage.messageType,
    );
  } catch (e) {
    DataDogLog.error(`Unable to send message to API: ${e}`, {
      ConversationId: conversationId,
      TraceId: traceId,
    });
    logger.error('Unable to send message to API', e);
    if (messageId) {
      setErrorState(messageId);
    }
  }
};

const getMessagesByConversationId = async (conversationId: string) => {
  if (!api) {
    return;
  }

  setIsSidebarCollapsed(true, 'lg');
  const messagesResult =
    await ApiService.getConversationMessagesById(conversationId);

  if (!messagesResult || messagesResult.length === 0) {
    return [];
  }

  const sortedMessages = [...messagesResult]
    .sort((a, b) => {
      if (!a.datetimeCreated || !b.datetimeCreated) {
        return -1;
      }
      if (a.datetimeCreated === b.datetimeCreated) {
        return a.messageType === 'userInput' ? -1 : 1;
      }
      let dateA = new Date(a.datetimeCreated),
        dateB = new Date(b.datetimeCreated);
      return (dateA as any) - (dateB as any);
    })
    .map(mapMessage);

  return sortedMessages;
};

export const deleteConversation = async (conversationId: string) => {
  if (!api) {
    return;
  }
  return ApiService.deleteConversationById(conversationId);
};

export const setApiConfig = async (config: any) => {
  apiConfig = config;
  if (apiConfig) {
    const token = await ApiService.acquireFreshToken();
    api = new ConversationsApi({
      ...apiConfig,
      apiKey: token,
    });
  }
};

export const setGptModel = (
  gptModel: string,
  wasAutomaticallySelected: boolean = false,
) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      gptModel: gptModel,
      gptModelAutomaticallySelected: wasAutomaticallySelected,
    };
  });
};

export const getDefaultIndexContexts = async (
  indexContexts: ConversationIndexType[] = [],
): Promise<ConversationIndexType[]> => {
  if (!indexContexts?.length) {
    return [];
  }
  return indexContexts.filter((context) =>
    DEFAULT_INDEX_CONTEXTS.includes(context.id),
  );
};

export const setCurrentConversation = async (conversationId?: string) => {
  setIsMessagesWindowScrolling(false);

  lastProcessedMessageId = undefined;

  await CurrentConversationStore.update((s) => {
    return {
      ...s,
      id: conversationId,
      hasLoaded: !conversationId,
      isLoading: !!conversationId,
      messages: [],
    };
  });
  setIsSidebarCollapsed(true, 'lg');

  if (streamController) {
    await streamController.abort();
    await sleep(200); // Give the stream time to close
  }

  // New Conversation
  if (!conversationId) {
    await CurrentConversationStore.update((s) => {
      return {
        ...initialState,
        fileContextFiles: s.fileContextFiles,
        indexContextIndexes: s.indexContextIndexes,
        gptModel: s.gptModel,
      };
    });

    return setIndexContexts(DEFAULT_INDEX_CONTEXTS);
  }

  // Load Previous Conversation
  try {
    const conversationResponse =
      await ApiService.getConversationById(conversationId);
    const id = conversationResponse.id;

    const originalMessages = await getMessagesByConversationId(id);
    const { messages, hasAutomaticSelection, gptModel } =
      getAutomaticModelSelectionData(originalMessages);

    setGptModel(gptModel, hasAutomaticSelection);

    return CurrentConversationStore.update((s) => {
      return {
        ...s,
        id: id,
        hasLoaded: true,
        isLoading: false,
        messages: [...messages],
        fileContexts: getUniqueSelectedFileContextIdsFromMessages(
          messages as string[],
        ),
        fileIdsUsedInConversation: getUniqueSelectedFileContextIdsFromMessages(
          messages as string[],
        ), // Storing this separately because fileContexts can be mutated by users selections
        indexContexts: getUniqueSelectedFileContextIdsFromMessages(
          messages as string[],
          'associatedIndexIds',
        ),
        indexIdsUsedInConversation: getUniqueSelectedFileContextIdsFromMessages(
          messages as string[],
          'associatedIndexIds',
        ), // Storing this separately because indexContexts can be mutated by users selections
      };
    });
  } catch (e) {
    logger.error('Unable to load conversation', e);
    setConversationLoading(false);
  }
};

export const resubmitLastMessage = async (errorMessageId) => {
  const errorMessage = CurrentConversationStore.getRawState().messages.find(
    (m) => m.messageId === errorMessageId,
  );
  if (!errorMessage) {
    return;
  }

  const messageToResubmit =
    CurrentConversationStore.getRawState().messages.find(
      (m) => m.messageId === errorMessage.previousMessageId,
    );

  if (!messageToResubmit) {
    return;
  }

  const conversationId = CurrentConversationStore.getRawState().id;
  if (!conversationId) {
    return;
  }

  await ApiService.updateConversationMessageById(
    conversationId,
    messageToResubmit.messageId,
    {
      ...messageToResubmit,
    },
  );
};

export const sendMessage = async (message: string) => {
  if (!api) {
    return;
  }
  const timeLogger = new TimeLogger();
  const currentConversationId = CurrentConversationStore.getRawState().id;

  return CurrentConversationStore.update((s) => {
    const currentMessages = [...s.messages];
    return {
      ...s,
      timeLogger: timeLogger,
      hasLoaded: true,
      id: currentConversationId,
      messages: [
        ...currentMessages,
        {
          message: message,
          messageId: s.messages.length,
          profileImg: '/profile.svg',
          messageType: 'userInput',
          associatedFileRecordIds: [...s.fileContexts],
          associatedIndexIds: [...s.indexContexts],
        },
      ],
    };
  });
};

export const renameConversation = async (
  conversationId: string,
  name: string,
) => {
  if (!api) {
    return;
  }

  await ApiService.updateConversationById(conversationId, {
    conversationName: name,
  });
};

export const appendToFileContexts = (fileContexts: string[]) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      fileContexts: [...s.fileContexts, ...fileContexts],
    };
  });
};

export const setFileContexts = (newFileContexts: any[]) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      fileContexts: [...newFileContexts],
    };
  });
};

export const setIndexContexts = (newIndexContexts: any[]) => {
  return CurrentConversationStore.update((s) => {
    return {
      ...s,
      indexContexts: [...newIndexContexts],
    };
  });
};

export const setConversationLoading = (isLoading: boolean) => {
  CurrentConversationStore.update((s) => {
    return {
      ...s,
      hasLoaded: !isLoading,
      isLoading,
      messages: [],
    };
  });
};

export const removeFileFromContexts = (fileContext: string) => {
  const currentConversationContexts =
    CurrentConversationStore.getRawState().fileContexts;
  const updatedContexts = currentConversationContexts.filter(
    (context) => context !== fileContext,
  );

  CurrentConversationStore.update((s) => {
    return {
      ...s,
      fileContexts: updatedContexts,
    };
  });
};

export const removeIndexFromContexts = (fileContext: string) => {
  const currentConversationContexts =
    CurrentConversationStore.getRawState().indexContexts;
  const updatedContexts = currentConversationContexts.filter(
    (context) => context !== fileContext,
  );

  CurrentConversationStore.update((s) => {
    return {
      ...s,
      indexContexts: updatedContexts,
    };
  });
};

export const getAutomaticModelSelectionData = (messages) => {
  const userInputs = messages.filter((m) => m.messageType === 'userInput');
  let hasAutomaticSelection =
    userInputs[userInputs.length - 1]?.model === 'automatic';
  let automaticIndex;
  let automaticallySelectedModel;

  const newMessages = messages?.length
    ? messages.map((m, i) => {
        if (m.model === 'automatic') {
          automaticIndex = i;
        }
        if (i === automaticIndex + 1) {
          automaticallySelectedModel = m.model;
        }
        return {
          ...m,
          isAutomaticSelection: m.model === automaticallySelectedModel,
        };
      })
    : messages;

  let newGptModel =
    newMessages.length && newMessages[newMessages.length - 1].model;
  const exhaustedGptModels = UserStore.getRawState().exhaustedGptModels;

  if (exhaustedGptModels.includes(newGptModel)) {
    newGptModel = getFallbackModelName();
    hasAutomaticSelection = false;
  }

  return {
    hasAutomaticSelection,
    messages: newMessages,
    gptModel: newGptModel,
  };
};

export const resetStore = () => {
  lastProcessedMessageId = undefined;

  CurrentConversationStore.update((s) => {
    return {
      ...initialState,
      fileContextFiles: s.fileContextFiles,
      indexContextIndexes: s.indexContextIndexes,
      gptModel: s.gptModel,
    };
  });

  setIndexContexts(DEFAULT_INDEX_CONTEXTS);
};
